import React from 'react';
import clsx from 'clsx';
import { InspectorHeader } from '../InspectorHeader/InspectorHeader';
import IconImage from '@/assets/icons/icon-image.svg?react';
import { SelectField } from '@/components/dropdowns/SelectField/SelectField';
import { SelectItem } from '@/components/ui/select';
import { Event } from '@/components/dropdowns/Event/Event';
import { ScrollArea } from '@/components/ui/scroll-area';
import NodeOpacitySettings from '@/components/inspector-fields/SliderField/NodeOpacitySettings';
import NodeImagePreviewSettings from '@/components/inspector-fields/ImagePreview/NodeImagePreviewSettings';
import NodeStrokeSettings from '@/components/inspector-fields/SliderField/NodeStrokeSettings';
import NodeColorField from '@/components/inspector-fields/ColorField/NodeColorField';
import { useNode } from '@craftjs/core';
import type { ImageProps } from '@builder4/shared/components/Image';
import NodeScaleSettings from '@/components/inspector-fields/SliderField/NodeScaleSettings';

import type { BackgroundPosition } from '@builder4/shared/types/layout';
import { LayoutElementV2 } from '@/components/inspector-fields/LayoutElement/LayoutElementV2';
import { useBreakpointAwareProp } from '@/hooks/useBreakpointAwareProp';
import NodeAnimationSettings from '@/components/inspector-fields/AnimationSettings/NodeAnimationSettings';
import NodeShadowField from '@/components/inspector-fields/ShadowField/NodeShadowField';

interface InspectorImageProps {
  className: string;
  text: string;
}

export const InspectorImage: React.FC<InspectorImageProps> = ({ className, text = 'Image' }) => {
  const {
    actions: { setProp },
    props,
    displayName,
  } = useNode((node) => ({
    props: node.data.props as ImageProps,
    displayName: node.data.displayName,
  }));

  const { getBPProp, setBPProp } = useBreakpointAwareProp(displayName);
  const fitType = getBPProp(props, 'fitType');
  const objectPosition = getBPProp(props, 'objectPosition');
  const backgroundPosition = getBPProp(props, 'backgroundPosition');

  return (
    <div
      className={clsx(
        'inspector bg-neutral-0 relative z-10 inline-flex h-[calc(100vh-52px)] max-w-[240px] flex-col border-l border-neutral-100',
        className,
      )}
    >
      <InspectorHeader className="max-w-[240px] text-neutral-900" icon={IconImage} text={text} />
      <ScrollArea className="h-[calc(100vh-104px)] overflow-hidden">
        <div className="flex flex-col gap-3 p-3">
          <LayoutElementV2
            className=""
            state="default"
            data-qa-selector="inspector-image-layout-element"
          />

          <div className="my-1 h-0 self-stretch border-t border-neutral-100" />

          <p className="pb-1 text-xs font-semibold text-neutral-900">Image</p>
          <NodeImagePreviewSettings data-qa-selector="inspector-image-preview" />
          <SelectField
            onValueChange={(value: string) => {
              setProp((props: ImageProps) => {
                setBPProp(props, 'fitType', value as ImageProps['fitType']);
              });
            }}
            className="max-w-[216px]"
            defaultValue={fitType || 'cover'}
            size="small"
            text="Mode"
            data-qa-selector="inspector-image-mode"
          >
            <SelectItem value="cover">Fill</SelectItem>
            <SelectItem value="contain">Fit</SelectItem>
            <SelectItem value="fill">Stretch</SelectItem>
            <SelectItem value="tile">Tile</SelectItem>
          </SelectField>
          <SelectField
            onValueChange={(value: string) => {
              if (fitType !== 'tile') {
                setProp((props: ImageProps) => {
                  setBPProp(props, 'objectPosition', value);
                });
              } else {
                setProp((props: ImageProps) => {
                  setBPProp(props, 'backgroundPosition', value as BackgroundPosition);
                });
              }
            }}
            className="max-w-[216px]"
            defaultValue={fitType !== 'tile' ? objectPosition : backgroundPosition}
            size="small"
            text="Position"
            data-qa-selector="inspector-image-position"
          >
            <SelectItem value="left top">Top Left</SelectItem>
            <SelectItem value="center top">Top Center</SelectItem>
            <SelectItem value="right top">Top Right</SelectItem>
            <SelectItem value="left center">Left</SelectItem>
            <SelectItem value="center center">Center</SelectItem>
            <SelectItem value="right center">Right</SelectItem>
            <SelectItem value="left bottom">Bottom Left</SelectItem>
            <SelectItem value="center bottom">Bottom Center</SelectItem>
            <SelectItem value="right bottom">Bottom Right</SelectItem>
          </SelectField>

          {fitType === 'tile' && <NodeScaleSettings data-qa-selector="inspector-image-scale" />}
          <NodeOpacitySettings data-qa-selector="inspector-image-opacity" />

          <div className="my-1 h-0 self-stretch border-t border-neutral-100" />

          <p className="pb-1 text-xs font-semibold text-neutral-900">Appearance</p>

          <NodeColorField
            propertyName="backgroundColor"
            solidPropertyName="bgSolid"
            gradientPropertyName="bgGradient"
            gradientEnabled
            label="Fill"
            data-qa-selector="inspector-image-fill"
          />

          <NodeColorField
            propertyName="borderColor"
            ensureStrokeVisibleProp="borderWidth"
            label="Stroke Color"
            data-qa-selector="inspector-image-stroke-color"
          />
          <NodeColorField
            propertyName="hoverBorderColor"
            ensureStrokeVisibleProp="borderWidth"
            label="Hover Stroke"
            data-qa-selector="inspector-image-hover-stroke"
          />

          <NodeStrokeSettings data-qa-selector="inspector-image-stroke" />

          <NodeShadowField data-qa-selector="inspector-image-shadow" />

          <div className="my-1 h-0 self-stretch border-t border-neutral-100" />

          <p className="pb-1 text-xs font-semibold text-neutral-900">Interaction</p>
          <Event
            className=""
            selectValue="nolink"
            text="Event"
            data-qa-selector="inspector-image-event"
          />
          <div className="my-1 h-0 self-stretch border-t border-neutral-100" />
          <NodeAnimationSettings showContinuousAnimation />
        </div>
      </ScrollArea>
    </div>
  );
};
